import { AnySupportedGeometry } from "@iventis/map-types";
import { Map } from "@iventis/mapbox-gl";
import { featureCollection } from "@turf/helpers";
import { Feature } from "geojson";
import { MapModuleLayer } from "../../types/store-schema";

/** Abstract class for a mapbox highlight layer. Use these highlight layers to apply highlighting around your features as and when needed */
export abstract class MapboxHighlightLayer {
    protected layerId: string;

    constructor(protected parentLayerId: string, protected map: Map) {
        this.layerId = `${parentLayerId}_highlight`;
    }

    public abstract addLayer(layer: MapModuleLayer, aboveLayerId?: string): void;

    public removeLayer(): void {
        const layer = this.map.getLayer(this.layerId);
        if (layer != null) {
            this.map.removeLayer(this.layerId);
        }
    }

    public addSource() {
        const source = this.map.getSource(this.layerId);
        if (source != null) {
            return;
        }
        this.map.addSource(this.layerId, {
            type: "geojson",
            data: featureCollection([]),
        });
    }

    public upsertSource(features: Feature<AnySupportedGeometry>[]) {
        const source = this.map.getSource(this.layerId);
        if (source != null && source.type === "geojson") {
            source.setData(featureCollection(features));
        } else {
            this.map.addSource(this.layerId, {
                type: "geojson",
                data: featureCollection(features),
            });
        }
    }

    public removeSource() {
        const source = this.map.getSource(this.layerId);
        if (source != null) {
            this.map.removeSource(this.layerId);
        }
    }

    public highlightObjects(objectIds: string[]) {
        const source = this.map.getSource(this.layerId);
        // If we're highlighting no objects, we only need to set the filter if the layer exists
        // If the layer doesn't exist, we don't need to set the filter, as it won't do anything
        if (this.map.getLayer(this.layerId) == null && objectIds.length === 0) {
            return;
        }
        if (source != null && source.type === "geojson") {
            this.map.setFilter(this.layerId, ["in", "id", ...objectIds]);
        }
    }

    public updateMapOrder(aboveLayerId: string) {
        this.map.moveLayer(this.layerId, aboveLayerId);
    }
}
